import React, {
  useEffect,
  useState,
} from "react";

import {
  Check,
  Plus,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";


function toLocalDateTime(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const adjusted = new Date(
    date.getTime() -
      date.getTimezoneOffset() *
        60000
  );

  return adjusted
    .toISOString()
    .slice(0, 16);
}


function toIsoDateTime(value) {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? null
    : date.toISOString();
}


function createInitialForm(report = {}) {
  return {
    diagnosis:
      report.diagnosis || "",
    work_performed:
      report.workPerformed || "",
    recommendations:
      report.recommendations || "",
    actual_start:
      toLocalDateTime(
        report.actualStart
      ),
    actual_end:
      toLocalDateTime(
        report.actualEnd
      ),
    actual_duration_minutes:
      report.actualDurationMinutes ??
      "",
    customer_name:
      report.customerName || "",
    customer_comment:
      report.customerComment || "",
    parts_used:
      Array.isArray(
        report.partsUsed
      )
        ? report.partsUsed.map(
            (part) => ({
              name: part.name || "",
              reference:
                part.reference || "",
              quantity:
                part.quantity ?? 1,
              unit:
                part.unit || "unité",
            })
          )
        : [],
  };
}


export default function WorkReportForm({
  report = {},
  editable = false,
  saving = false,
  allowFreeTextParts = true,
  onSubmit,
}) {
  const [form, setForm] =
    useState(
      createInitialForm(report)
    );


  useEffect(() => {
    setForm(
      createInitialForm(report)
    );
  }, [report]);


  const updateField = (
    field,
    value
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };


  const updatePart = (
    index,
    field,
    value
  ) => {
    setForm((current) => ({
      ...current,
      parts_used:
        current.parts_used.map(
          (part, partIndex) =>
            partIndex === index
              ? {
                  ...part,
                  [field]: value,
                }
              : part
        ),
    }));
  };


  const addPart = () => {
    setForm((current) => ({
      ...current,
      parts_used: [
        ...current.parts_used,
        {
          name: "",
          reference: "",
          quantity: 1,
          unit: "unité",
        },
      ],
    }));
  };


  const removePart = (
    index
  ) => {
    setForm((current) => ({
      ...current,
      parts_used:
        current.parts_used.filter(
          (
            _,
            partIndex
          ) =>
            partIndex !== index
        ),
    }));
  };


  const submit = (
    event
  ) => {
    event.preventDefault();

    if (!editable) {
      return;
    }

    if (
      form.actual_start &&
      form.actual_end
    ) {
      const start = new Date(
        form.actual_start
      );

      const end = new Date(
        form.actual_end
      );

      if (end < start) {
        toast.error(
          "L’heure de fin doit être postérieure à l’heure de début."
        );

        return;
      }
    }

    for (
      const part
      of form.parts_used
    ) {
      if (!part.name.trim()) {
        toast.error(
          "Renseignez le nom de chaque pièce utilisée."
        );

        return;
      }

      if (
        Number(
          part.quantity
        ) <= 0
      ) {
        toast.error(
          "La quantité d’une pièce doit être supérieure à zéro."
        );

        return;
      }
    }

    onSubmit({
      diagnosis:
        form.diagnosis.trim(),
      work_performed:
        form.work_performed.trim(),
      recommendations:
        form.recommendations.trim(),
      actual_start:
        toIsoDateTime(
          form.actual_start
        ),
      actual_end:
        toIsoDateTime(
          form.actual_end
        ),
      actual_duration_minutes:
        form.actual_duration_minutes ===
        ""
          ? null
          : Number(
              form.actual_duration_minutes
            ),
      customer_name:
        form.customer_name.trim(),
      customer_comment:
        form.customer_comment.trim(),
      parts_used:
        form.parts_used.map(
          (part) => ({
            name:
              part.name.trim(),
            reference:
              part.reference.trim(),
            quantity:
              Number(
                part.quantity
              ),
            unit:
              part.unit.trim() ||
              "unité",
          })
        ),
    });
  };


  const inputClass =
    "w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm outline-none transition focus:border-[var(--do-accent)] disabled:cursor-not-allowed disabled:opacity-70";


  return (
    <form
      onSubmit={submit}
      className="space-y-5"
      data-testid="work-report-form"
    >
      <div>
        <label
          htmlFor="work-report-diagnosis"
          className="label-eyebrow mb-1.5 block"
        >
          Diagnostic
        </label>

        <textarea
          id="work-report-diagnosis"
          value={form.diagnosis}
          onChange={(event) =>
            updateField(
              "diagnosis",
              event.target.value
            )
          }
          disabled={
            !editable ||
            saving
          }
          rows={4}
          maxLength={4000}
          data-testid="work-report-diagnosis"
          className={`${inputClass} resize-y`}
          placeholder="Constat, panne identifiée, contrôles effectués…"
        />
      </div>

      <div>
        <label
          htmlFor="work-report-performed"
          className="label-eyebrow mb-1.5 block"
        >
          Travaux réalisés
        </label>

        <textarea
          id="work-report-performed"
          value={
            form.work_performed
          }
          onChange={(event) =>
            updateField(
              "work_performed",
              event.target.value
            )
          }
          disabled={
            !editable ||
            saving
          }
          rows={6}
          maxLength={8000}
          data-testid="work-report-performed"
          className={`${inputClass} resize-y`}
          placeholder="Décrire précisément les opérations réalisées…"
        />
      </div>

      <div>
        <label
          htmlFor="work-report-recommendations"
          className="label-eyebrow mb-1.5 block"
        >
          Recommandations
        </label>

        <textarea
          id="work-report-recommendations"
          value={
            form.recommendations
          }
          onChange={(event) =>
            updateField(
              "recommendations",
              event.target.value
            )
          }
          disabled={
            !editable ||
            saving
          }
          rows={4}
          maxLength={4000}
          data-testid="work-report-recommendations"
          className={`${inputClass} resize-y`}
          placeholder="Entretien à prévoir, surveillance, prochaine opération…"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div>
          <label
            htmlFor="work-report-start"
            className="label-eyebrow mb-1.5 block"
          >
            Début réel
          </label>

          <input
            id="work-report-start"
            type="datetime-local"
            value={
              form.actual_start
            }
            onChange={(event) =>
              updateField(
                "actual_start",
                event.target.value
              )
            }
            disabled={
              !editable ||
              saving
            }
            data-testid="work-report-start"
            className={inputClass}
          />
        </div>

        <div>
          <label
            htmlFor="work-report-end"
            className="label-eyebrow mb-1.5 block"
          >
            Fin réelle
          </label>

          <input
            id="work-report-end"
            type="datetime-local"
            value={
              form.actual_end
            }
            onChange={(event) =>
              updateField(
                "actual_end",
                event.target.value
              )
            }
            disabled={
              !editable ||
              saving
            }
            data-testid="work-report-end"
            className={inputClass}
          />
        </div>

        <div>
          <label
            htmlFor="work-report-duration"
            className="label-eyebrow mb-1.5 block"
          >
            Durée réelle (minutes)
          </label>

          <input
            id="work-report-duration"
            type="number"
            min="0"
            max="10080"
            value={
              form.actual_duration_minutes
            }
            onChange={(event) =>
              updateField(
                "actual_duration_minutes",
                event.target.value
              )
            }
            disabled={
              !editable ||
              saving
            }
            data-testid="work-report-duration"
            className={inputClass}
          />
        </div>
      </div>

      <section>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="label-eyebrow">
              Consommation
            </div>

            <h3 className="mt-1 font-heading font-black">
              {allowFreeTextParts
                ? "Pièces utilisées"
                : "Pièces historiques saisies librement"}
            </h3>
          </div>

          {!allowFreeTextParts && (
            <p className="max-w-xl text-xs text-[var(--do-muted)]">
              Les nouvelles consommations sont désormais gérées directement depuis le stock GMAO ci-dessous. Les anciennes lignes restent conservées en lecture seule.
            </p>
          )}

          {editable && allowFreeTextParts && (
            <button
              type="button"
              onClick={addPart}
              disabled={saving}
              data-testid="work-report-add-part"
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--do-border)] px-3 py-2 text-sm font-heading font-semibold disabled:opacity-50"
            >
              <Plus
                size={16}
                weight="bold"
              />

              Ajouter une pièce
            </button>
          )}
        </div>

        {form.parts_used.length ===
        0 ? (
          <div className="mt-3 rounded-lg border border-dashed border-[var(--do-border)] p-4 text-sm text-[var(--do-muted)]">
            Aucune pièce renseignée.
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            {form.parts_used.map(
              (
                part,
                index
              ) => (
                <div
                  key={index}
                  className="grid grid-cols-1 gap-3 rounded-xl border border-[var(--do-border)] p-3 lg:grid-cols-[2fr_1.2fr_0.7fr_0.8fr_auto]"
                  data-testid={
                    `work-report-part-${index}`
                  }
                >
                  <input
                    type="text"
                    value={part.name}
                    onChange={(event) =>
                      updatePart(
                        index,
                        "name",
                        event.target.value
                      )
                    }
                    disabled={
                      !editable ||
                      saving ||
                      !allowFreeTextParts
                    }
                    placeholder="Nom de la pièce"
                    className={inputClass}
                  />

                  <input
                    type="text"
                    value={
                      part.reference
                    }
                    onChange={(event) =>
                      updatePart(
                        index,
                        "reference",
                        event.target.value
                      )
                    }
                    disabled={
                      !editable ||
                      saving ||
                      !allowFreeTextParts
                    }
                    placeholder="Référence"
                    className={inputClass}
                  />

                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={
                      part.quantity
                    }
                    onChange={(event) =>
                      updatePart(
                        index,
                        "quantity",
                        event.target.value
                      )
                    }
                    disabled={
                      !editable ||
                      saving ||
                      !allowFreeTextParts
                    }
                    placeholder="Qté"
                    className={inputClass}
                  />

                  <input
                    type="text"
                    value={part.unit}
                    onChange={(event) =>
                      updatePart(
                        index,
                        "unit",
                        event.target.value
                      )
                    }
                    disabled={
                      !editable ||
                      saving ||
                      !allowFreeTextParts
                    }
                    placeholder="Unité"
                    className={inputClass}
                  />

                  {editable && allowFreeTextParts && (
                    <button
                      type="button"
                      onClick={() =>
                        removePart(
                          index
                        )
                      }
                      disabled={saving}
                      aria-label="Supprimer la pièce"
                      className="inline-flex items-center justify-center rounded-lg border border-[var(--do-border)] px-3 py-2 text-[var(--do-accent)] disabled:opacity-50"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <label
            htmlFor="work-report-customer"
            className="label-eyebrow mb-1.5 block"
          >
            Nom du client / signataire
          </label>

          <input
            id="work-report-customer"
            type="text"
            value={
              form.customer_name
            }
            onChange={(event) =>
              updateField(
                "customer_name",
                event.target.value
              )
            }
            disabled={
              !editable ||
              saving
            }
            maxLength={160}
            data-testid="work-report-customer-name"
            className={inputClass}
          />
        </div>

        <div>
          <label
            htmlFor="work-report-customer-comment"
            className="label-eyebrow mb-1.5 block"
          >
            Commentaire client
          </label>

          <textarea
            id="work-report-customer-comment"
            value={
              form.customer_comment
            }
            onChange={(event) =>
              updateField(
                "customer_comment",
                event.target.value
              )
            }
            disabled={
              !editable ||
              saving
            }
            rows={3}
            maxLength={2000}
            data-testid="work-report-customer-comment"
            className={`${inputClass} resize-y`}
          />
        </div>
      </div>

      {editable && (
        <div className="flex justify-end border-t border-[var(--do-border)] pt-4">
          <button
            type="submit"
            disabled={saving}
            data-testid="work-report-submit"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--do-blue)] px-4 py-2 text-sm font-heading font-bold text-[var(--do-on-accent)] disabled:opacity-50"
          >
            <Check
              size={17}
              weight="bold"
            />

            {saving
              ? "Enregistrement…"
              : "Enregistrer le compte rendu"}
          </button>
        </div>
      )}
    </form>
  );
}
